const API_BASE = '/api/signaling'

type ApiResult<T> = { success: true; data: T } | { success: false; error: string }

async function apiCall<T>(
	path: string,
	method: string,
	body?: unknown
): Promise<ApiResult<T>> {
	try {
		const response = await fetch(`${API_BASE}${path}`, {
			method,
			headers: { 'Content-Type': 'application/json' },
			body: body ? JSON.stringify(body) : undefined,
		})

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({ error: 'Request failed' }))
			return { success: false, error: errorData.error || `HTTP ${response.status}` }
		}

		const data = await response.json()
		return { success: true, data }
	} catch (error) {
		return {
			success: false,
			error: error instanceof Error ? error.message : 'Network error',
		}
	}
}

// ===============================
// Type Definitions
// ===============================

export interface CreateRoomRequest {
	roomId: string
	slotId: string
	offer: string
	connectionMode: 'internet' | 'local'
	hostId?: string
}

export interface CreateRoomResponse {
	roomId: string
	slotId: string
	expiresAt: number
}

export interface JoinRoomRequest {
	roomId: string
	slotId: string
	answer: string
	playerId: string
}

export interface JoinRoomResponse {
	slotId: string
}

export interface AddSlotRequest {
	roomId: string
	slotId: string
	offer: string
}

export interface AddSlotResponse {
	roomId: string
	slotId: string
}

export interface PollResponse {
	answers: Array<{
		slotId: string
		answer: string
		playerId: string
		timestamp: number
	}>
}

export interface FetchOfferResponse {
	offer: string
	roomId: string
	slotId: string
	expiresAt: number
}

// ===============================
// API Functions
// ===============================

export async function signalingCreate(request: CreateRoomRequest): Promise<ApiResult<CreateRoomResponse>> {
	return apiCall<CreateRoomResponse>('/create', 'POST', request)
}

export async function signalingJoin(request: JoinRoomRequest): Promise<ApiResult<JoinRoomResponse>> {
	return apiCall<JoinRoomResponse>('/join', 'POST', request)
}

export async function signalingPoll(roomId: string): Promise<ApiResult<PollResponse>> {
	return apiCall<PollResponse>(`/poll/${roomId}`, 'GET')
}

export async function signalingFetchOffer(
	roomId: string,
	slotId: string
): Promise<ApiResult<FetchOfferResponse>> {
	return apiCall<FetchOfferResponse>(`/offer/${roomId}/${slotId}`, 'GET')
}

export async function signalingAddSlot(request: AddSlotRequest): Promise<ApiResult<AddSlotResponse>> {
	return apiCall<AddSlotResponse>('/add-slot', 'POST', request)
}
